import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    setIsUpdating(true);
    await updateQuantity(productId, newQuantity);
    setIsUpdating(false);
  };

  const handleRemove = async (productId) => {
    setIsUpdating(true);
    await removeFromCart(productId);
    setIsUpdating(false);
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const cartItems = cart?.items || [];
  const isEmpty = cartItems.length === 0;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-12 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

            {isEmpty ? (
              <Card className="text-center py-16">
                <CardContent>
                  <ShoppingCart className="h-24 w-24 text-gray-300 mx-auto mb-6" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
                  <p className="text-gray-600 mb-8">Start adding some books and products to your cart!</p>
                  <Link to="/store">
                    <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
                      Browse Store
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Cart Items */}
                <div className="lg:col-span-2 space-y-4">
                  {cartItems.map((item) => (
                    <Card key={item.product_id}>
                      <CardContent className="p-6">
                        <div className="flex gap-6">
                          <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          <div className="flex-grow">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                                <p className="text-sm text-gray-500 capitalize">{item.product_type}</p>
                              </div>
                              <button
                                onClick={() => handleRemove(item.product_id)}
                                disabled={isUpdating}
                                className="text-red-600 hover:text-red-700 p-2"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>

                            <div className="flex items-center justify-between mt-4">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                                  disabled={isUpdating || item.quantity <= 1}
                                  className="p-2 border rounded-md hover:bg-gray-100 disabled:opacity-50"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="text-lg font-medium w-12 text-center">{item.quantity}</span>
                                <button
                                  onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                                  disabled={isUpdating}
                                  className="p-2 border rounded-md hover:bg-gray-100 disabled:opacity-50"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="text-right">
                                <p className="text-2xl font-bold text-gray-900">
                                  ${(item.price * item.quantity).toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-500">
                                  ${item.price.toFixed(2)} each
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                  <Card className="sticky top-24">
                    <CardHeader>
                      <CardTitle>Order Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between text-gray-600">
                        <span>Items ({getCartCount()})</span>
                        <span>${getCartTotal().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Shipping</span>
                        <span className="text-green-600">FREE</span>
                      </div>
                      <div className="border-t pt-4">
                        <div className="flex justify-between text-xl font-bold text-gray-900">
                          <span>Total</span>
                          <span>${getCartTotal().toFixed(2)}</span>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex-col gap-3">
                      <Button
                        onClick={handleCheckout}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                        size="lg"
                      >
                        Proceed to Checkout
                      </Button>
                      <Link to="/store" className="w-full">
                        <Button variant="outline" className="w-full">
                          Continue Shopping
                        </Button>
                      </Link>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Cart;
